import { useAgentComplianceReviewPlatformUrlQuery } from '@client/graphql/__generated__/main-operations';

interface UseAgentComplianceReviewPlatformUrlArgs {
  /** Share-link token (`?token=` query param). */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
  /**
   * When true the query is not fired. The caller skips until the agent has a
   * document open (the platform CTA is only shown then), so the page load and
   * post-mutation refetches never trigger the SkySlope API call this resolves.
   */
  skip?: boolean;
}

interface UseAgentComplianceReviewPlatformUrlResult {
  platformUrl: string | null | undefined;
  loading: boolean;
}

/**
 * Loads the agent compliance review platform deep-link URL (SkySlope checklist,
 * DotLoop loop, etc.) for the current share link, ON DEMAND.
 *
 * Resolving this URL calls the originating platform's API (e.g. SkySlope
 * getSale/getListing, which can trigger a token refresh), so it is deliberately
 * NOT part of the overview query — the overview loads (and refetches after
 * every mutation) without ever touching the platform API. The caller fires this
 * only once a document is open (via `skip`), and it must NOT be added to any
 * `refetchQueries`. `cache-first` keeps the result stable across re-renders.
 */
export const useAgentComplianceReviewPlatformUrl = ({
  token,
  ingestionFolderUuid,
  skip,
}: UseAgentComplianceReviewPlatformUrlArgs): UseAgentComplianceReviewPlatformUrlResult => {
  const { data, loading } = useAgentComplianceReviewPlatformUrlQuery({
    variables: {
      input: {
        token: token ?? '',
        ingestionFolderUuid: ingestionFolderUuid ?? '',
      },
    },
    fetchPolicy: 'cache-first',
    skip: skip === true || !token || !ingestionFolderUuid,
    // The "Fix in SkySlope" CTA is optional — if the platform URL can't be
    // resolved, hide the CTA rather than surfacing a global error toast on this
    // no-login page.
    context: { skipGlobalErrorHandler: true },
  });

  return {
    platformUrl: data?.agentComplianceReviewPlatformUrl,
    loading,
  };
};
